import { ConflictLevelsStatusEnum, TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import type { TaskFilterKey } from './taskFilterOptions';
import { type TaskTabSort, buildTaskSortBy } from './taskSort';

/**
 * Swaps the tab's sort options as its Status filter takes in resolved claims,
 * so the sort is on what those cards show. `resolvedIncluded` replaces the
 * fields while Resolved is one of several statuses; `resolvedOnly` replaces
 * the default and fields while it is the only one.
 */
type TaskTabConfigSort = TaskTabSort & {
  resolvedIncluded?: Pick<TaskTabSort, 'fields'>;
  resolvedOnly?: Pick<TaskTabSort, 'default' | 'fields'>;
};

export type TaskTabSegment = 'competing' | 'drafts' | 'potential';

/**
 * How many tasks each Home section shows before the rest are only reachable on
 * the Tasks page. Governs the page size each section requests, its skeleton row
 * count and its "View All" threshold, so they cannot disagree.
 */
export const HOME_VISIBLE_TASKS = 3;

export interface TaskTabConfig {
  /** Statuses still listed on the tab but left out of its count badges. */
  countExcludedStatuses?: ConflictLevelsStatusEnum[];
  emptyDescription: string;
  /** Shown when the workspace has no tasks of this type at all. */
  emptyTitle: string;
  /** Filters this tab offers, in display order. Client is still agent-only. */
  filters: TaskFilterKey[];
  /** Drafts has a single filter, so it sits inline with no Filters toggle. */
  hasFiltersToggle: boolean;
  /** Competing claims spreads its filters across the row; the others keep a fixed width. */
  isFilterRowFullWidth: boolean;
  label: string;
  /** An info banner above the tab's search and filters. */
  notice?: { description: string; title: string };
  searchPlaceholder: string;
  segment: TaskTabSegment;
  /** Sort options on the tab; its default also orders the Home section. */
  sort: TaskTabConfigSort;
  type: TaskTypeEnum;
}

/**
 * Single source of truth for the three Tasks-page tabs — used by the layout
 * (tab bar + count badges), the tab pages (labels/placeholders/empty states),
 * and the Home "View All" deep-links.
 */
export const TASK_TABS: TaskTabConfig[] = [
  {
    emptyDescription:
      'If we find a potential claim you may be able to register, you’ll see it displayed here.',
    emptyTitle: 'There are no potential claims',
    filters: ['dueDate', 'client'],
    hasFiltersToggle: true,
    isFilterRowFullWidth: false,
    label: 'Potential Claims',
    searchPlaceholder: 'Search your potential claims',
    segment: 'potential',
    sort: {
      default: { field: 'dueDate', order: 'asc' },
      fields: ['dueDate', 'estimatedValue', 'title'],
    },
    type: TaskTypeEnum.PotentialClaim,
  },
  {
    // A resolved claim needs no action, so it stays listed but isn't counted.
    countExcludedStatuses: [ConflictLevelsStatusEnum.Resolved],
    emptyDescription:
      'If any of your titles receive a competing claim you’ll see it displayed here.',
    emptyTitle: 'There are no competing claims',
    filters: ['status', 'dueDate', 'client', 'otherClaimant'],
    hasFiltersToggle: true,
    isFilterRowFullWidth: true,
    label: 'Competing Claims',
    // MSR2 syncs a submitted competing claim back asynchronously, so its task
    // can stay listed for a while after the member has acted on it.
    notice: {
      description:
        'Please note it may take some time for a Competing Claim task to be marked as done once you have submitted your changes. Please check back here periodically and we thank you for your patience.',
      title: 'Competing Claims Task Processing Times',
    },
    searchPlaceholder: 'Search your competing claims',
    segment: 'competing',
    sort: {
      default: { field: 'dueDate', order: 'asc' },
      fields: ['dueDate', 'estimatedValue', 'title', 'status'],
      // Matches `countExcludedStatuses`: resolved claims need no action.
      pinned: ['resolved:asc'],
      // Resolved cards show Est. Resolved Value; open cards have none, so that
      // sort leads with resolved claims by value (`isUnpinned`).
      resolvedIncluded: {
        fields: [
          'dueDate',
          'estimatedValue',
          'resolvedValue',
          'title',
          'status',
        ],
      },
      // Every card then shows Est. Resolved Value and neither a due date nor
      // another status, so those sorts would change nothing (APP-2631).
      resolvedOnly: {
        default: { field: 'resolvedValue', order: 'desc' },
        fields: ['resolvedValue', 'title'],
      },
    },
    type: TaskTypeEnum.CompetingClaim,
  },
  {
    emptyDescription:
      'Any incomplete registrations will be displayed as a draft.',
    emptyTitle: 'There are no drafts',
    filters: ['client'],
    hasFiltersToggle: false,
    isFilterRowFullWidth: false,
    label: 'Drafts',
    searchPlaceholder: 'Search your drafts',
    segment: 'drafts',
    sort: {
      default: { field: 'created', order: 'desc' },
      fields: ['created', 'title'],
    },
    type: TaskTypeEnum.DraftRegistration,
  },
];

export const taskTabByType = (type: TaskTypeEnum): TaskTabConfig | undefined =>
  TASK_TABS.find((tab) => tab.type === type);

/** The tab's sort for the current Status filter (see `TaskTabConfigSort`). */
export const taskTabSort = (
  tab: TaskTabConfig | undefined,
  {
    includesResolved,
    isResolvedOnly,
  }: { includesResolved: boolean; isResolvedOnly: boolean },
): TaskTabSort | undefined => {
  if (!tab) return undefined;

  const { resolvedIncluded, resolvedOnly, ...sort } = tab.sort;

  if (isResolvedOnly && resolvedOnly) return { ...sort, ...resolvedOnly };

  if (includesResolved && resolvedIncluded) {
    return { ...sort, ...resolvedIncluded };
  }

  return sort;
};

/** A tab's default order, for the Home section showing its first few tasks. */
export const defaultTaskSortBy = (type: TaskTypeEnum) => {
  const tab = taskTabByType(type);

  return tab && buildTaskSortBy(tab.sort, tab.sort.default);
};

/** Absolute path to a Tasks-page tab, e.g. `/workspace/123/tasks/potential`. */
export const taskTabPath = (
  workspaceId: string,
  segment: TaskTabSegment,
): string => `/workspace/${workspaceId}/tasks/${segment}`;
